import React from 'react';
import styled from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, PSmall } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import {
  useGetCompany,
  useGetCompanyOfficeHours,
} from '../../../hooks/use-hydra/admin';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Button from '../../elements/Button';
import {
  assignLearnersToCohortMentor,
  AssignLearnersToCohortMentorParams,
  CompanyResponse,
} from '../../../api/admin';
import { SmallPortalTooltip, SmallTooltip } from '../../Tooltip';
import { useMutation } from 'react-query';
import useAuth from '../../../hooks/use-auth';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  TableHead,
  TableRow,
  TableHeader,
  Table,
  TableWrapper,
  TableCell,
  TableBody,
} from '../../Table';
import { SmallBadge } from '../../elements/Badge';
import LearnerAssignmentCell from '../Cells/LearnerAssignmentCell';
import { toast } from 'react-toastify';
import {
  TooltipContent,
  TooltipDetail,
  TooltipTitle,
} from '../Progress/Tooltip';
import { calculateLearningPlanProgress } from '../Progress';

dayjs.extend(utc);
dayjs.extend(timezone);

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 95vw;
  margin: 20px 0;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;

type MentorOfficeHourRow = {
  mentor: CompanyResponse['mentors'][number];
  officeTime: Date;
  combinationId: string;
  isFirstMentorOccurrence: boolean;
  assignments: {
    learnerId: number;
    sessionId: number;
    learnerName: string;
    learnerEmail: string;
  }[];
};

export type LearnerOption = {
  label: string;
  sessionId: number;
  learnerId: number;
  email: string;
  officeHourPreferences: Date[];
  role: string;
  progress: number | null;
  skillRatings: {
    iconUrl: string;
    rating: number;
    topic: string;
  }[];
};

const columnHelper = createColumnHelper<MentorOfficeHourRow>();

const LearnersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
`;

const LearnerBadgesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

const LearnerBadge = styled(SmallBadge)`
  background-color: ${({ theme }) => theme.colors.bgActive} !important;
  border-radius: 200px !important;
  padding: 4px 12px !important;
  border: 1px solid ${({ theme }) => theme.colors.bgActive} !important;
  transition: all 0.3s ease-in-out;

  p {
    font-size: 13px;
    line-height: 18px;
    color: ${({ theme }) => theme.colors.textActive} !important;
    white-space: nowrap;
  }
`;

const LearnerAssignmentScrollContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 2px;
  max-width: 100%;
`;

const GroupMembersScrollContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 2px;
  max-width: 100%;
`;

export const LearnerBadgeTooltip = ({
  email,
  officeHoursPrefs,
  skillRatings,
  progress,
  role,
}: {
  email: string;
  officeHoursPrefs?: Date[];
  skillRatings?: Record<string, number>;
  progress: number | null;
  role: string;
}) => (
  <TooltipContent>
    <TooltipTitle>Email</TooltipTitle>
    <TooltipDetail>{email}</TooltipDetail>
    <TooltipTitle style={{ marginTop: 8 }}>
      Office Hours Preferences
    </TooltipTitle>
    {officeHoursPrefs && officeHoursPrefs.length > 0 ? (
      officeHoursPrefs.map((time, index) => {
        return (
          <TooltipDetail key={index}>{`${convertTimestampToLocalTime(
            time
          )}`}</TooltipDetail>
        );
      })
    ) : (
      <TooltipDetail>No preferences specified</TooltipDetail>
    )}
    <TooltipTitle style={{ marginTop: 8 }}>
      Skills Self - Assessment
    </TooltipTitle>
    <TooltipDetail>
      {skillRatings && Object.keys(skillRatings).length > 0 ? (
        Object.entries(skillRatings).map(([skill, rating]) => (
          <TooltipDetail key={skill}>
            {skill}: {rating}
          </TooltipDetail>
        ))
      ) : (
        <TooltipDetail>No skills assessment provided</TooltipDetail>
      )}
    </TooltipDetail>
    <TooltipTitle style={{ marginTop: 8 }}>Plan Progress</TooltipTitle>
    <TooltipDetail>
      {progress !== null && progress !== undefined ? `${progress}%` : 'N/A'}
    </TooltipDetail>
    <TooltipTitle style={{ marginTop: 8 }}>Role</TooltipTitle>
    <TooltipDetail>{role}</TooltipDetail>
  </TooltipContent>
);

export function convertTimestampToLocalTime(timestamp: Date) {
  const local = dayjs.utc(timestamp).local();
  const minutes = local.format('mm');
  return local.format(`dddd ${minutes === '00' ? 'ha' : 'h:mma'}`);
}

export default function AssignLearnersToMentorModal({
  isOpen,
  onClose,
  companyId,
  selectedSessionIds,
  cycleId,
}: {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  selectedSessionIds: number[];
  cycleId: number;
}) {
  const { getTokenSilently } = useAuth();
  const {
    data,
    refetch: refetchCompany,
    isFetching: isFetchingCompany,
  } = useGetCompany(companyId);
  const { data: officeHours, refetch: refetchOfficeHours } =
    useGetCompanyOfficeHours(companyId);

  // State - store processed data, don't rebuild on every render
  const [tableData, setTableData] = React.useState<MentorOfficeHourRow[]>([]);
  const [learnerOptions, setLearnerOptions] = React.useState<LearnerOption[]>(
    []
  );
  const [assignments, setAssignments] = React.useState<{
    [combinationId: string]: {
      learnerId: number;
      sessionId: number;
      learnerName: string;
      learnerEmail: string;
    }[];
  }>({});

  const cohort = data?.cohorts.find((c) => c.cycleId === cycleId);

  // Mutation for assigning learners to mentors
  const assignLearnersMutation = useMutation({
    mutationFn: async () => {
      const token = await getTokenSilently();

      const apiAssignments: AssignLearnersToCohortMentorParams['assignments'] =
        [];

      Object.entries(assignments).forEach(([combinationId, assignmentList]) => {
        if (assignmentList.length > 0) {
          // Parse combinationId to get mentor ID and office hour index
          const [mentorIdStr, officeHourIndexStr] = combinationId.split('-');
          const mentorId = parseInt(mentorIdStr);
          const officeHourIndex = parseInt(officeHourIndexStr);

          // Get the first office hour time for this combination
          const firstOfficeHourTime =
            cohort?.officeHourTimes?.[officeHourIndex];

          if (firstOfficeHourTime) {
            const sessionIds = assignmentList.map(
              (assignment) => assignment.sessionId
            );

            apiAssignments.push({
              mentorProfileId: mentorId,
              firstOfficeHourTimestamp: new Date(firstOfficeHourTime),
              cohortInviteIds: sessionIds,
            });
          }
        }
      });

      await assignLearnersToCohortMentor(token, {
        companyId,
        cohortId: cycleId,
        assignments: apiAssignments,
      });
    },
    onSuccess: async () => {
      await refetchCompany();
      await refetchOfficeHours();
      toast.success('Learners assigned to mentors successfully');
      onClose();
    },
    onError: (error) => {
      console.error('Failed to assign learners:', error);
      toast.error('Something went wrong when assigning learners to mentors');
    },
  });

  const cohortValue = {
    label: `${dayjs.utc(cohort?.startDate).format('MMM D, YYYY')} - ${dayjs
      .utc(cohort?.endDate)
      .format('MMM D, YYYY')}`,
    value: cohort?.cycleId,
  };

  const getSkillRatings = React.useCallback(
    (skillRatings: Record<string, number>) => {
      return Object.entries(skillRatings).map(([skill, rating]) => {
        const moduleWithSameTopic = data?.modules.find(
          (m) => m.topic === skill
        );
        const projectWithSameTopic = data?.projects.find(
          (p) => p.topic === skill
        );
        return {
          iconUrl:
            (moduleWithSameTopic?.topicIconUrl ||
              projectWithSameTopic?.topicIconUrl) ??
            '',
          rating: rating,
          topic: skill,
        };
      });
    },
    [data]
  );

  // Initialize data once when modal opens or data changes
  const initializeData = React.useCallback(() => {
    if (isFetchingCompany || !isOpen || !data) {
      return;
    }

    const companyMentors = data.mentors.filter((m) => m.isCompanyMentor);
    const cohortOfficeHours = cohort?.officeHourTimes;

    // Build learner options
    const allSelectedLearners =
      cohort?.learners.filter(
        (learner) =>
          selectedSessionIds.includes(learner.sessionId) && !learner.mentor
      ) || [];

    const options = allSelectedLearners.map((learner) => {
      const learnerProfile = data?.learners.find(
        (l) => l.id === learner.learnerId
      );
      let progress = null;
      if (learnerProfile && typeof learnerProfile.id === 'number' && data) {
        progress = calculateLearningPlanProgress(learnerProfile.id, data);
      }
      const skillRatings = getSkillRatings(
        learner.onboardingAnswers?.skillRatings || {}
      );
      return {
        label: learner.name,
        value: learner.sessionId,
        sessionId: learner.sessionId,
        learnerId: learner.learnerId,
        email: learner.email,
        officeHourPreferences:
          learner.onboardingAnswers?.officeHoursTimes || [],
        role: learnerProfile?.onboardingAnswers?.role || 'Unknown',
        progress,
        skillRatings,
      };
    });

    // Build table data
    const rows: MentorOfficeHourRow[] = [];
    if (companyMentors && cohortOfficeHours) {
      companyMentors.forEach((mentor) => {
        const mentorCohortOfficeHours =
          cohort?.mentors.find((m) => m.profileId === mentor.id)
            ?.officeHoursAvailability || [];

        const validOfficeHours = cohortOfficeHours.filter((officeTime) => {
          const hasOfficeHour = officeHours?.officeHoursGroups.find(
            (g) =>
              g.mentor?.mentorProfileId === mentor.id &&
              g.officeHours.find((oh) => oh.timestamp === officeTime)
          );
          const isAvailable = mentorCohortOfficeHours.includes(officeTime);
          return hasOfficeHour || isAvailable;
        });

        validOfficeHours.forEach((officeTime, index) => {
          const combinationId = `${mentor.id}-${index}`;
          rows.push({
            mentor,
            officeTime,
            combinationId,
            isFirstMentorOccurrence: index === 0,
            assignments: assignments[combinationId] || [],
          });
        });
      });
    }

    setLearnerOptions(options);
    setTableData(rows);
  }, [
    data,
    cohort,
    officeHours,
    selectedSessionIds,
    isFetchingCompany,
    isOpen,
    assignments,
    getSkillRatings,
  ]);

  React.useEffect(() => {
    initializeData();
  }, [initializeData]);

  const handleAssignmentChange = React.useCallback(
    (combinationId: string, newAssignments: LearnerOption[]) => {
      const simpleAssignments = newAssignments.map((a) => ({
        learnerId: a.learnerId,
        sessionId: a.sessionId,
        learnerName: a.label,
        learnerEmail: a.email,
      }));

      setAssignments((prev) => ({
        ...prev,
        [combinationId]: simpleAssignments,
      }));
    },
    []
  );

  // Update table data when assignments change
  React.useEffect(() => {
    if (tableData.length > 0) {
      setTableData((prevData) =>
        prevData.map((row) => ({
          ...row,
          assignments: assignments[row.combinationId] || [],
        }))
      );
    }
  }, [assignments, tableData.length]);

  // Calculate which learners are assigned and which are available
  const assignedLearnerIds = React.useMemo(() => {
    const assignedIds = new Set<number>();
    Object.values(assignments).forEach((assignmentList) => {
      assignmentList.forEach((assignment) => {
        assignedIds.add(assignment.learnerId);
      });
    });
    return assignedIds;
  }, [assignments]);

  const unassignedLearners = React.useMemo(() => {
    return learnerOptions.filter(
      (learner) => !assignedLearnerIds.has(learner.learnerId)
    );
  }, [learnerOptions, assignedLearnerIds]);

  // Check if all learners are assigned
  // Determine button disabled state and tooltip message
  const isButtonDisabled =
    assignLearnersMutation.isLoading || assignedLearnerIds.size === 0;
  const getDisabledTooltipMessage = () => {
    if (assignedLearnerIds.size === 0) {
      return 'You have not assigned any learners';
    }
    if (assignLearnersMutation.isLoading) {
      return 'Assignment in progress...';
    }
    return '';
  };

  const getAvailableLearnersForCombination = React.useCallback(
    (combinationId: string) => {
      // For each combination, show:
      // 1. Learners already assigned to THIS combination (so they appear selected)
      // 2. Learners not assigned to ANY combination
      const currentAssignments = assignments[combinationId] || [];
      const currentAssignmentIds = new Set(
        currentAssignments.map((a) => a.learnerId)
      );

      return learnerOptions.filter(
        (learner) =>
          currentAssignmentIds.has(learner.sessionId) || // Already assigned to this combination
          !assignedLearnerIds.has(learner.sessionId) // Not assigned to any combination
      );
    },
    [learnerOptions, assignments, assignedLearnerIds]
  );

  const userLocal = dayjs();
  let userOffset = userLocal.format('Z'); // e.g., '+01:00'
  if (userOffset.endsWith(':00')) {
    userOffset = userOffset.replace(':00', '');
  }

  const columns = React.useMemo(
    () => [
      columnHelper.display({
        id: 'mentor',
        header: 'Mentor',
        cell: ({ row }) => {
          const mentor = row.original.mentor;
          console.log('mentor', mentor);
          const combinationId = row.original.combinationId;
          const isFirstOccurrence = row.original.isFirstMentorOccurrence;

          if (!isFirstOccurrence) {
            return <div style={{ height: '20px' }}></div>;
          }

          return (
            <>
              <div className={`mentor-${combinationId}`}>{mentor.name}</div>
              <SmallTooltip anchorSelect={`.mentor-${combinationId}`}>
                {mentor.email}
              </SmallTooltip>
            </>
          );
        },
        meta: { styles: { width: '15%' } },
      }),
      columnHelper.display({
        id: 'officeTime',
        header: `Time`,
        cell: ({ row }) => {
          const officeTime = row.original.officeTime;
          return convertTimestampToLocalTime(officeTime);
        },
        meta: { styles: { width: '13%' } },
      }),
      columnHelper.display({
        id: 'current-grou-members',
        header: 'Current Members',
        cell: ({ row }) => {
          const mentorId = row.original.mentor.id;
          const existingGroupLearnerIds = officeHours?.officeHoursGroups
            ?.find(
              (g) =>
                g.mentor?.mentorProfileId === mentorId &&
                g.officeHours.find(
                  (oh) => oh.timestamp === row.original.officeTime
                )
            )
            ?.members.map((m) => m.learnerId);

          const existingGroupLearners = existingGroupLearnerIds?.map((id) => {
            return data?.learners.find((l) => l.id === id);
          });

          if (!existingGroupLearners?.length) {
            return '-';
          }

          return (
            <GroupMembersScrollContainer>
              {existingGroupLearners.map((l) => {
                if (!l) return null;
                // Find the full learner data for tooltip details
                const cohortLearner = cohort?.learners.find(
                  (learner) => learner.learnerId === l.id
                );
                const officeHoursPrefs =
                  cohortLearner?.onboardingAnswers?.officeHoursTimes;
                const skillRatings =
                  cohortLearner?.onboardingAnswers?.skillRatings;
                let progress = null;
                if (typeof l.id === 'number' && data) {
                  progress = calculateLearningPlanProgress(l.id, data);
                }
                const role = l.onboardingAnswers?.role || 'Unknown';
                return (
                  <React.Fragment key={l.id}>
                    <LearnerBadge
                      className={`group-learner-${l.id}`}
                      label={l.name}
                      isPrimary={false}
                      // style={{ minWidth: '150px', maxWidth: '150px' }}
                    />
                    <SmallPortalTooltip anchorSelect={`.group-learner-${l.id}`}>
                      <LearnerBadgeTooltip
                        email={l.email}
                        officeHoursPrefs={officeHoursPrefs}
                        skillRatings={skillRatings}
                        progress={progress}
                        role={role}
                      />
                    </SmallPortalTooltip>
                  </React.Fragment>
                );
              })}
            </GroupMembersScrollContainer>
          );
        },
        meta: { styles: { maxWidth: '250px', minWidth: '250px' } },
      }),
      columnHelper.display({
        id: 'assignedLearners',
        header: 'Member Assignments',
        cell: ({ row }) => {
          const combinationId = row.original.combinationId;
          const currentAssignments = row.original.assignments;

          const assignedLearners = currentAssignments.map((a) => {
            // Find the full learner data to get office hour preferences and role
            const cohortLearner = cohort?.learners.find(
              (l) => l.sessionId === a.learnerId
            );
            const learnerProfile = data?.learners.find(
              (l) => l.id === a.learnerId
            );

            let progress = null;
            if (
              learnerProfile &&
              typeof learnerProfile.id === 'number' &&
              data
            ) {
              progress = calculateLearningPlanProgress(learnerProfile.id, data);
            }

            const skillRatings = getSkillRatings(
              cohortLearner?.onboardingAnswers?.skillRatings || {}
            );

            return {
              label: a.learnerName,
              sessionId: a.sessionId,
              learnerId: a.learnerId,
              email: a.learnerEmail,
              officeHourPreferences:
                cohortLearner?.onboardingAnswers?.officeHoursTimes || [],
              role: learnerProfile?.onboardingAnswers?.role || 'Unknown',
              progress,
              skillRatings,
            };
          });

          return (
            <LearnerAssignmentScrollContainer>
              <LearnerAssignmentCell
                combinationId={combinationId}
                assignedLearners={assignedLearners}
                availableLearners={getAvailableLearnersForCombination(
                  combinationId
                )}
                onChange={handleAssignmentChange}
                officeTime={row.original.officeTime}
              />
            </LearnerAssignmentScrollContainer>
          );
        },
        meta: { styles: { minWidth: '400px', maxWidth: '50%' } },
      }),
    ],
    [
      handleAssignmentChange,
      getAvailableLearnersForCombination,
      cohort?.learners,
      officeHours?.officeHoursGroups,
      data,
      getSkillRatings,
    ]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Reset assignments when modal closes or selection context changes
  React.useEffect(() => {
    if (!isOpen) {
      setAssignments({});
    }
  }, [isOpen]);

  // Optionally, also reset when selection context changes
  React.useEffect(() => {
    if (assignLearnersMutation.isLoading) {
      return;
    }
    setAssignments({});
  }, [
    selectedSessionIds,
    cycleId,
    companyId,
    assignLearnersMutation.isLoading,
  ]);

  if (!isOpen) {
    return null;
  }

  const localTzLabel = `GMT${userOffset}`.toUpperCase();
  const hasMentorAvailabilities =
    cohort?.mentors?.length &&
    cohort?.mentors?.every((m) => m.officeHoursAvailability?.length);

  return (
    <Wrapper>
      <Row>
        <Header>Assign Learners to Mentor</Header>
        <CloseButton onClick={onClose} />
      </Row>

      <LearnersContainer>
        <PSmall>
          Cohort: <b>{cohortValue.label}</b>
        </PSmall>
        <PSmall style={{ marginTop: 8 }}>
          You have {unassignedLearners.length} unassigned learner
          {unassignedLearners.length !== 1 ? 's' : ''} remaining.
        </PSmall>
        {unassignedLearners.length > 0 && (
          <LearnerBadgesContainer>
            {unassignedLearners.map((learner) => {
              const fullLearnerData = cohort?.learners.find(
                (l) => l.sessionId === learner.sessionId
              );

              const officeHours =
                fullLearnerData?.onboardingAnswers?.officeHoursTimes;
              const skillRatings =
                fullLearnerData?.onboardingAnswers?.skillRatings;
              return (
                <React.Fragment key={learner.sessionId}>
                  <LearnerBadge
                    className={`learner-${learner.sessionId}`}
                    label={`${learner.label} (${officeHours?.length})`}
                    isPrimary={false}
                  />
                  <SmallPortalTooltip
                    anchorSelect={`.learner-${learner.sessionId}`}
                  >
                    <LearnerBadgeTooltip
                      email={learner.email}
                      officeHoursPrefs={officeHours}
                      skillRatings={skillRatings}
                      progress={learner.progress}
                      role={learner.role}
                    />
                  </SmallPortalTooltip>
                </React.Fragment>
              );
            })}
          </LearnerBadgesContainer>
        )}
      </LearnersContainer>

      <PSmall>
        We'll create a weekly recurring event for the duration of the cohort.
        All times shown are in {localTzLabel}.
      </PSmall>

      {tableData.length > 0 ? (
        <TableWrapper>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      meta={header.column.columnDef.meta}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} meta={cell.column.columnDef.meta}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrapper>
      ) : (
        <PSmall style={{ marginTop: 8, opacity: 0.7 }}>
          {!data?.mentors?.filter((m) => m.isCompanyMentor)?.length
            ? 'No company mentors available'
            : !cohort?.officeHourTimes?.length
            ? 'No office hours configured for this cohort'
            : !hasMentorAvailabilities
            ? 'Mentor availability not configured'
            : 'No combinations available'}
        </PSmall>
      )}

      <Row style={{ marginTop: 24 }}>
        <Button
          label="Assign Learners to Mentors"
          showIconBeforeLabel
          faIcon="fa-solid fa-paper-plane"
          type="primary"
          onClick={() => {
            assignLearnersMutation.mutate();
          }}
          isDisabled={isButtonDisabled}
          isLoading={assignLearnersMutation.isLoading}
          tooltip={isButtonDisabled ? getDisabledTooltipMessage() : ''}
        />
      </Row>
    </Wrapper>
  );
}
